import { useState } from "react";
import dayjs from "dayjs";
import I18n from "common/i18n";
import Constants from "common/constant";
import { ChatImageViewer } from "common/elements";
import { btnDelay, usePlaySound } from "common/utils";
import { Message, MessageRole } from "../types";

interface MessageBubbleProps {
  message: Message;
  isStreaming?: boolean;
  userIcon?: string;
}

const formatTime = (dateString: string) => {
  return dayjs(dateString).format("HH:mm");
};

const TypingDots = () => (
  <div className="flex flex-row gap-[6px] items-center h-[32px]">
    {[0, 1, 2].map((i) => (
      <div
        key={i}
        className="w-[10px] h-[10px] rounded-full bg-textcolor-black/40"
        style={{
          animation: "typingBounce 1.4s infinite ease-in-out both",
          animationDelay: `${i * 0.16}s`,
        }}
      />
    ))}
  </div>
);

export const MessageBubble = ({ message, isStreaming = false, userIcon }: MessageBubbleProps) => {
  const play = usePlaySound();
  const [previewUrl, setPreviewUrl] = useState<string>();
  const isUser = message.role === MessageRole.USER;
  const showTypingDots = isStreaming && !message.content;

  return (
    <div className={`w-full flex flex-row items-start gap-3 ${isUser ? "flex-row-reverse" : ""}`}>
      {/* アバター */}
      <div className="flex-col-center w-[52px] h-[52px] rounded-full bg-white flex-shrink-0 drop-shadow-[1px_1px_2px_rgba(63,74,97,0.4)] overflow-hidden">
        {isUser && userIcon ? (
          <img src={userIcon} className="w-[44px] h-[44px] rounded-full object-cover" />
        ) : isUser ? (
          <div className="flex-col-center w-[44px] h-[44px] rounded-full bg-gradient-to-br from-puzzle-blue to-[#2766ae]">
            <span className="text-[24px]" role="img">
              👦
            </span>
          </div>
        ) : (
          <img
            src={`${Constants.assetHost}/assets/images/little_pal.png`}
            className="w-full h-full object-contain"
          />
        )}
      </div>

      {/* メッセージコンテンツ */}
      <div
        className={`flex flex-col gap-[6px] max-w-[80%] ${isUser ? "items-end" : "items-start"}`}
      >
        <span className="text-[14px] leading-[18px] text-textcolor-black opacity-70 px-1">
          {isUser ? I18n.t("MSG_ASSISTANT_YOU") : I18n.t("MSG_ASSISTANT_NAME")}
        </span>
        <div className={`flex flex-col gap-2 ${isUser ? "items-end" : "items-start"}`}>
          {/* 画像表示（複数対応） */}
          {message.images && message.images.length > 0 && (
            <div className="flex flex-row flex-wrap gap-2 max-w-[400px]">
              {message.images.map((img, idx) => (
                <div
                  key={idx}
                  className="max-w-[180px] rounded-2xl overflow-hidden cursor-pointer"
                  onClick={() =>
                    btnDelay(() => {
                      play();
                      setPreviewUrl(img.url);
                    })
                  }
                >
                  <img src={img.url} className="w-full h-auto object-cover" />
                </div>
              ))}
            </div>
          )}
          {/* テキストバブル */}
          <div className={`flex flex-row items-end gap-2 ${isUser ? "flex-row-reverse" : ""}`}>
            {(message.content || showTypingDots) && (
              <div
                className={`py-[14px] px-[18px] rounded-[20px] text-[20px] leading-[32px] text-textcolor-black whitespace-pre-wrap break-words ${
                  isUser ? "bg-green-80 rounded-tr-[6px]" : "bg-gray-20 rounded-tl-[6px]"
                }`}
              >
                {showTypingDots ? <TypingDots /> : message.content}
              </div>
            )}
            <span className="text-[12px] leading-[16px] text-textcolor-black opacity-50 flex-shrink-0">
              {formatTime(message.createdAt)}
            </span>
          </div>
        </div>
      </div>

      <ChatImageViewer
        visible={!!previewUrl}
        url={previewUrl}
        close={() => setPreviewUrl(undefined)}
      />
    </div>
  );
};
